import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { SignatureBook } from "../main-screen/admin/signature-book/SignatureBook";
import "./AdminPanel.css";

import { EditGuestModal } from "./components/edit-guest-modal/EditGuestModal";
import { DeleteGuestModal } from "./components/delete-guest-modal/DeleteGuestModal";
import LogoutButton from "../login/logout/LogoutButton";

export type Guest = {
  name: string;
  token: string;
  invited: number;
  confirmed: number;
  hasConfirmed: boolean;
  phoneNumber: string;
  guests?: string[];
  table?: number;
};

const generateToken = () => Math.random().toString(36).substr(2, 16);

export const AdminPanel = () => {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [editingToken, setEditingToken] = useState<string | null>(null);
  const [tokenToDelete, setTokenToDelete] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [invited, setInvited] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [table, setTable] = useState<number | undefined>(undefined);
  const [guestNames, setGuestNames] = useState<string[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [errors, setErrors] = useState<{
    name?: string;
    invited?: string;
    phoneNumber?: string;
    guestNames?: string[];
  }>({});

  const fetchGuests = async () => {
    const snap = await getDocs(collection(db, "guests"));
    setGuests(snap.docs.map((d) => d.data() as Guest));
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!name.trim()) newErrors.name = "El nombre es obligatorio.";
    if (!phoneNumber.trim())
      newErrors.phoneNumber = "El teléfono es obligatorio.";
    if (invited < 1 || invited > 10)
      newErrors.invited = "El número de pases debe estar entre 1 y 10.";

    if (invited > 1) {
      const guestErrors = guestNames.map((g) =>
        g.trim() === "" ? "Este nombre es obligatorio." : ""
      );
      if (guestErrors.some(Boolean)) newErrors.guestNames = guestErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    const token = generateToken();

    const newGuest: Guest = {
      name,
      token,
      invited,
      confirmed: 0,
      hasConfirmed: false,
      phoneNumber,
      guests: guestNames,
      table,
    };

    await setDoc(doc(db, "guests", token), newGuest);
    clearForm();
    fetchGuests();
  };

  const clearForm = () => {
    setName("");
    setPhoneNumber("");
    setInvited(1);
    setTable(undefined);
    setGuestNames([]);
    setErrors({});
  };

  const handleDelete = async (token: string) => {
    await deleteDoc(doc(db, "guests", token));
    fetchGuests();
  };

  const handleUpdate = async (updated: Guest) => {
    await setDoc(doc(db, "guests", updated.token), updated);
    setEditingToken(null);
    fetchGuests();
  };

  useEffect(() => {
    fetchGuests();
  }, []);

  useEffect(() => {
    if (invited < 1) return;
    const numGuests = invited - 1;
    const updated = [...guestNames];
    while (updated.length < numGuests) updated.push("");
    while (updated.length > numGuests) updated.pop();
    setGuestNames(updated);
  }, [invited]);

  return (
    <div className="admin-panel">
      <h2>Panel de administración</h2>
      <LogoutButton />

      {/* Formulario de creación */}
      <div className="admin-form">
        <h4>Crear nuevo invitado</h4>

        <label>Nombre del invitado principal</label>
        <input value={name} onChange={(e) => setName(e.target.value)} />
        {errors.name && <p className="form-error">{errors.name}</p>}

        <label>Número de pases</label>
        <input
          type="number"
          min={1}
          max={10}
          value={invited === 0 ? "" : invited}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "") {
              setInvited(0);
              return;
            }

            const parsed = Number(val);

            if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
              setInvited(parsed);
            }
          }}
          onBlur={() => {
            if (invited < 1 || isNaN(invited)) setInvited(1);
            else if (invited > 10) setInvited(10);
          }}
        />
        {errors.invited && <p className="form-error">{errors.invited}</p>}

        <label>Teléfono</label>
        <input
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
        {errors.phoneNumber && (
          <p className="form-error">{errors.phoneNumber}</p>
        )}

        <label>Mesa asignada</label>
        <input
          type="number"
          value={table ?? ""}
          onChange={(e) => setTable(parseInt(e.target.value))}
        />

        {invited > 1 &&
          guestNames.map((guest, index) => (
            <div key={index}>
              <label>Acompañante {index + 1}</label>
              <input
                value={guest}
                onChange={(e) => {
                  const updated = [...guestNames];
                  updated[index] = e.target.value;
                  setGuestNames(updated);
                }}
              />
              {errors.guestNames?.[index] && (
                <p className="form-error">{errors.guestNames[index]}</p>
              )}
            </div>
          ))}

        <button onClick={handleCreate}>Crear invitado</button>
      </div>

      {/* Tabla de invitados */}
      <h4>Lista de invitados</h4>
      <div className="admin-table-header">
        <button
          className="search-toggle"
          onClick={() => setShowSearch((prev) => !prev)}
        >
          🔍
        </button>
        {showSearch && (
          <input
            type="text"
            placeholder="Buscar por nombre..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}
      </div>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Token</th>
              <th>Entradas</th>
              <th>Confirmados</th>
              <th>¿Confirmado?</th>
              <th>Mesa</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {guests
              .filter((g) =>
                g.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
              )
              .sort((a, b) => {
                const tableA = a.table ?? 9999; // si no tiene mesa, lo manda al final
                const tableB = b.table ?? 9999;
                return tableA - tableB;
              })
              .map((guest) => (
                <tr key={guest.token}>
                  <td>{guest.name}</td>
                  <td>{guest.token}</td>
                  <td>{guest.invited}</td>
                  <td>{guest.confirmed}</td>
                  <td>
                    {guest.hasConfirmed ? (
                      guest.confirmed > 0 ? (
                        <span style={{ color: "green", fontWeight: "bold" }}>
                          ✅ Confirmado
                        </span>
                      ) : (
                        <span style={{ color: "red", fontWeight: "bold" }}>
                          ❌ No asiste
                        </span>
                      )
                    ) : (
                      <span style={{ color: "#8c906c", fontWeight: "bold" }}>
                        ⏳ Pendiente
                      </span>
                    )}
                  </td>
                  <td>{guest.table ?? "-"}</td>
                  <td>
                    <button onClick={() => setEditingToken(guest.token)}>
                      ✏️
                    </button>
                    <button
                      onClick={() => setTokenToDelete(guest.token)}
                      style={{ marginLeft: "0.5rem" }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <SignatureBook />

      {/* Modal para editar */}
      {editingToken && (
        <EditGuestModal
          guest={guests.find((g) => g.token === editingToken) ?? null}
          onClose={() => setEditingToken(null)}
          onSave={handleUpdate}
        />
      )}

      {/* Modal para eliminar */}
      {tokenToDelete && (
        <DeleteGuestModal
          token={tokenToDelete}
          guest={guests.find((g) => g.token === tokenToDelete)}
          onConfirm={() => {
            handleDelete(tokenToDelete);
            setTokenToDelete(null);
          }}
          onCancel={() => setTokenToDelete(null)}
        />
      )}
    </div>
  );
};
